import React, { useEffect } from "react";
import Editor from "../../../form/Editor";
import Input from "../../../form/Input";
import Select from "../../../form/Select";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import useMutation from "../../../../hooks/useMutation";
import {
  bannerAddEdit,
  bannerDetails,
} from "../../../../services/bannersService";
import FileUploadSingle from "../../../form/FileUploadSingle";
import Textarea from "../../../form/Textarea";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { HomePageForm } from "../pageManagement/HomePageForm";
import { VepetersPageForm } from "../pageManagement/VpetersPageForm";
import { ApplyPageForm } from "../pageManagement/ApplyPageForm";
import { BlogPageForm } from "../pageManagement/BlogPageForm";
import { getDetailsObject } from "../../../../util/Flag";
import { useSearchParams } from "react-router-dom";
import useFetch from "../../../../hooks/useFetch";
import ImageOrVideoUploadSingle from "../../../form/ImageOrVideoUpload";
import { TakeActionForm } from "../pageManagement/TakeActionForm";

const validation = {
  title: { required: true, message: "title is required" },
  image: { required: true, message: "image is required" },
  bannerType: { required: true, message: "Select banner for is required" },
  description: { required: true, message: "description is required" },
};

const ManageBanner = () => {
  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    resetField,
    formValidate,
  } = useForm({
    initialState: {
      button: [{ label: "", link: "" }],
    },
    validationRules: validation,
  });

  const [searchParams] = useSearchParams();
  const idFromUrl = searchParams.get("pageId");
  const navigate = useNavigate();

  const { data, updateData, isFetching, refetch } = useFetch({
    dependencies: ["bannerDtls"],
    fetchFunction: async () => {
      const res = await bannerDetails({
        id: idFromUrl,
      });
      return res?.data;
    },
  });

  useEffect(() => {
    if (data?._id) {
      const bannerDetailsMap = {
        home: [
          "testimonial",
          "partner",
          "videoSection",
          "existence",
          "scholarAward",
          "beneficiary",
          "fundDistribution",
        ],
        apply: ["eligibility", "timeline"],
        "v'Peters": [
          "publication",
          "firstSubSection",
          "secondSubSection",
          "thirdSubSection",
          "inspire",
          "impact",
        ],
        // projects: ["projectOverview"],
        updatesAndBlogs: ["blog"],
        // contact: ["contactInfo"],
        // takeAction: ["actionSection"],
      };
      const detailsToSet = bannerDetailsMap[data?.bannerType] || [];

      // Extract dynamic fields from details
      const extractedDetails = detailsToSet.reduce((acc, key) => {
        acc[`${key}Title`] = data?.details?.[key]?.title || "";
        acc[`${key}Desc`] = data?.details?.[key]?.description || "";
        return acc;
      }, {});

      setFieldsValue({
        ...data,
        image: data?.image?.url,
        button:
          data?.button?.map((item) => ({
            label: item?.buttonText,
            link: item?.buttonLink,
          })) || [],
        ...extractedDetails,
        existenceCount: data?.details?.existence?.count,
        scholarAwardCount: data?.details?.scholarAward?.count,
        beneficiaryCount: data?.details?.beneficiary?.count,
        fundDistributionCount: data?.details?.fundDistribution?.count,
        firstSubSectionCount: data?.details?.firstSubSection?.count,
        secondSubSectionCount: data?.details?.secondSubSection?.count,
        thirdSubSectionCount: data?.details?.thirdSubSection?.count,
        videoSectionDesc: data?.details?.videoSection?.link,
        inspireImage: data?.details?.inspire?.image?.url,
        impactImage: data?.details?.impact?.image?.url,
        eligibilityImage: data?.details?.eligibility?.eligibilityImage?.url,
        timelineImage: data?.details?.timeline?.image?.url,
        faqImage: data?.details?.faq?.image?.url,
        subSectionimage: data?.details?.firstSubSection?.image?.url,
        eligibilitySubTitle: data?.details?.eligibility?.subtitle,
        faqTitle: data?.details?.faq?.title,
        blogBannerImage: data?.blogBannerImage?.url,
        actionImage: data?.details?.takeAction?.image?.url,
        sectionImage: data?.details?.takeAction?.sectionImage?.url,
      });
    }
  }, [data]);

  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      const payload = {
        ...val,
        button: val?.button?.map((item) => ({
          buttonText: item?.label,
          buttonLink: item?.link,
        })),
        ...(data?._id && { id: data?._id }),
        details: getDetailsObject(val.bannerType, val),
      };
      delete payload?.timelineDesc;
      delete payload?.timelineTitle;
      delete payload?.eligibilitySubTitle;

      return await bannerAddEdit(payload);
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        toast.success(res?.message);
        navigate("/admin/pages");
      } else {
        toast.warning(res?.message || res?.error);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  function buttonGenerateHandler() {
    setFieldsValue({
      button: [...values?.button, { label: "", link: "" }],
    });
  }

  const getBannerComponent = (bannerType, props) => {
    const bannerComponents = {
      home: <HomePageForm {...props} />,
      "v'Peters": <VepetersPageForm {...props} />,
      apply: <ApplyPageForm {...props} />,
      // projects: <ProjectsBannerForm {...props} />,
      updatesAndBlogs: <BlogPageForm {...props} />,
      // contact: <ContactBannerForm {...props} />,
      takeAction: <TakeActionForm {...props} />,
    };

    return bannerComponents[bannerType] || null;
  };

  return (
    <form onSubmit={(event) => handleSubmit(event, mutate)}>
      <div className="space-y-8">
        <div className="font-bold text-center text-lg xl:text-3xl text-chestnut-rose">
          Pages Management
        </div>
        <div className="grid grid-cols-12 gap-x-4 gap-y-4">
          <div className="relative col-span-8">
            <Input
              label="Banner Title"
              inputPlaceholder={"Enter Banner Title"}
              inputName="title"
              value={values?.title || ""}
              onChange={handleChange}
              {...formValidate(errors, "title")}
            />
          </div>
          <div className="relative col-span-4">
            <Select
              label={"Select banner for"}
              placeholder="Select banner for"
              selectedValue={values?.bannerType}
              dropdownData={[
                { name: "Home", value: "home" },
                { name: "V'Peters", value: "v'Peters" },
                { name: "Apply", value: "apply" },
                { name: "Projects", value: "projects" },
                { name: "Updates & Blogs", value: "updatesAndBlogs" },
                { name: "Contact", value: "contact" },
                { name: "Take Action", value: "takeAction" },
                { name: "Login", value: "login" },
                { name: "Signup", value: "sign-up" },
              ]}
              isDisabled={data?._id}
              setSelectedValue={(val) => {
                handleChange({ target: { name: "bannerType", value: val } });
              }}
              {...formValidate(errors, "bannerType")}
            />
          </div>
          {values?.bannerType === "login" ||
          values?.bannerType === "sign-up" ? (
            <div className="relative col-span-12">
              <ImageOrVideoUploadSingle
                label={"Upload Banner Image or Video"}
                labelClasses={"!mb-2"}
                value={values?.image}
                onChange={(file) =>
                  handleChange({ target: { name: "image", value: file } })
                }
                accept=".jpg,.jpeg,.png,.mp4,.webm,image/*,video/*"
                {...formValidate(errors, "image")}
              />
            </div>
          ) : (
            <div className="relative col-span-12">
              <FileUploadSingle
                label={"Upload Banner Image"}
                labelClasses={"!mb-2"}
                value={values?.image}
                onChange={(file) =>
                  handleChange({ target: { name: "image", value: file } })
                }
                {...formValidate(errors, "image")}
              />
            </div>
          )}

          <div className="relative col-span-12">
            <Editor
              label="Description"
              value={values?.description}
              onChange={(val) =>
                handleChange({ target: { name: "description", value: val } })
              }
              {...formValidate(errors, "description")}
            />
          </div>
          <div className="relative col-span-12 space-y-4">
            <div className="space-y-4">
              {values?.button?.map((item, i) => (
                <div key={i?.toString()} className="flex items-end space-x-4">
                  <div className="flex-shrink flex-grow w-1/2">
                    <Input
                      label={"Button label " + (i + 1)}
                      value={item?.label || ""}
                      inputPlaceholder={"Enter button label"}
                      onChange={(e) => {
                        setFieldsValue({
                          button: values?.button?.map((item, index) =>
                            i === index
                              ? { ...item, label: e.target.value }
                              : item
                          ),
                        });
                      }}
                    />
                  </div>
                  <div className="flex-shrink flex-grow w-1/2">
                    <Input
                      label={"Button link " + (i + 1)}
                      value={item?.link || ""}
                      inputPlaceholder={"Enter button link"}
                      onChange={(e) => {
                        setFieldsValue({
                          button: values?.button?.map((item, index) =>
                            i === index
                              ? { ...item, link: e.target.value }
                              : item
                          ),
                        });
                      }}
                    />
                  </div>
                  <div className="flex-shrink-0 !ml-0">
                    <Button
                      buttonIcon={"fa-regular fa-trash-can"}
                      buttonClasses={"!bg-transparent !text-chestnut-rose"}
                      buttonIconPosition={"left"}
                      buttonFunction={() => {
                        setFieldsValue({
                          button: values?.button?.filter(
                            (item, index) => i !== index
                          ),
                        });
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center">
              <Button
                buttonClasses={
                  "!text-chestnut-rose !h-6 !p-0 !rounded-none !bg-transparent"
                }
                buttonLabel={"Add Action"}
                buttonLabelClasses={"!text-sm"}
                buttonFunction={() => buttonGenerateHandler()}
              />
            </div>
          </div>
          <div className="relative col-span-12">
            <Input
              label="Content Title"
              inputPlaceholder={"Enter Content Title"}
              inputName="contentTitle"
              value={values?.contentTitle || ""}
              onChange={handleChange}
              {...formValidate(errors, "contentTitle")}
            />
          </div>
          <div className="relative col-span-12">
            <Editor
              label="Content Description"
              value={values?.contentDescription}
              onChange={(val) =>
                handleChange({
                  target: { name: "contentDescription", value: val },
                })
              }
              {...formValidate(errors, "contentDescription")}
            />
          </div>
          <div className="relative col-span-12">
            {getBannerComponent(values.bannerType, {
              values,
              handleChange,
              errors,
              formValidate,
            })}
          </div>
          <div className="relative col-span-12">
            <h4 className="font-semibold text-lg mb-2">SEO Section</h4>
            <div className="grid grid-cols-12 gap-4">
              <div className="relative col-span-6">
                <Input
                  label="SEO Title"
                  inputPlaceholder={"Enter SEO Title"}
                  inputName="seoTitle"
                  value={values?.seoTitle || ""}
                  onChange={handleChange}
                  {...formValidate(errors, "seoTitle")}
                />
              </div>
              <div className="relative col-span-6">
                <Textarea
                  label="SEO Description"
                  inputPlaceholder={"Enter SEO Description"}
                  inputName="seoDescription"
                  inputValue={values?.seoDescription || ""}
                  onChange={handleChange}
                  {...formValidate(errors, "seoDescription")}
                />
              </div>
              <div className="relative col-span-6">
                <Input
                  label="SEO Keyword"
                  inputPlaceholder={"Enter SEO Keyword"}
                  inputName="seoKeyword"
                  value={values?.seoKeyword || ""}
                  onChange={handleChange}
                  {...formValidate(errors, "seoKeyword")}
                />
              </div>
              <div className="relative col-span-6">
                <Textarea
                  label="SEO Author Content"
                  inputPlaceholder={"Enter SEO Description"}
                  inputName="seoAuthor"
                  inputValue={values?.seoAuthor || ""}
                  onChange={handleChange}
                  {...formValidate(errors, "seoAuthor")}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="relative col-span-4 mt-5">
          <div className="flex items-center justify-center gap-4">
            <Button
              buttonLabel={"Back"}
              buttonClasses={
                "!rounded-full !px-8 !h-12 !border !border-chestnut-rose !bg-transparent !text-chestnut-rose hover:!bg-chestnut-rose hover:!text-white"
              }
              buttonLabelClasses={"mx-auto"}
              buttonHasLink
              buttonLink={"/admin/pages"}
            />
            <Button
              buttonType="submit"
              buttonLabel={data?._id ? "Update Page" : "Save Page"}
              buttonClasses={
                "!rounded-full !px-8 !h-12 !border !border-chestnut-rose !bg-transparent !text-chestnut-rose hover:!bg-chestnut-rose hover:!text-white"
              }
              buttonLabelClasses={"mx-auto"}
              {...(isLoading && {
                loading: true,
                buttonIconPosition: "left",
                isDisable: true,
              })}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default ManageBanner;
